import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, Alert, Image, TouchableWithoutFeedback } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { Plus, X, Send, Bug, Trash2, ChevronDown, ChevronUp, Paperclip, MessageSquare, DollarSign, Download, Eye, File, Image as ImageIcon, Video, FileText, Clock, Calendar, ChevronRight, CheckCircle2, AlertCircle } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { SUPABASE_URL } from '../lib/constants';
import { useLocation } from 'react-router-dom';
import { sendServiceRequestEmail } from '../lib/email';
import { ServiceRequestFileUpload } from '../components/ServiceRequestFileUpload';
import { shadows } from '../lib/styles';
import { downloadFile } from '../lib/fileDownload';
import { getAccessibleHomeIds } from '../lib/homeMembership';
import BottomSheet from '../components/ui/BottomSheet';
import { useServiceRequestWorkflow } from '../hooks/useServiceRequestWorkflow';
import { ClarifyQuestion } from '../components/ClarifyQuestionsForm';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface ServiceRequestNote {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
}

interface ServiceRequestQuote {
  id: string;
  service_request_id: string;
  service_provider_id?: string | null;
  amount?: number | null;
  notes?: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  quote_date?: string | null;
  expires_at?: string | null;
  created_at: string;
  service_provider?: {
    id: string;
    company_name: string;
    email?: string;
    phone?: string;
    address?: string;
  } | null;
}

interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  created_at: string;
}

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  home_id: string | null;
  home?: {
    address: string;
    city: string;
    state: string;
  };
  owner_id?: string;
  owner_assigned_at?: string;
  owner_assigned_by?: string;
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

const priorities = [
  { 
    value: 'low', 
    label: 'Low', 
    description: 'Non-urgent maintenance or improvements. We\'ll schedule this at our convenience.'
  },
  { 
    value: 'normal', 
    label: 'Normal', 
    description: 'Standard service request. We\'ll address this during regular scheduling.'
  },
  { 
    value: 'urgent', 
    label: 'Urgent', 
    description: 'Requires immediate attention. We\'ll aim to send someone out within 24 hours.'
  }
];

const ServiceRequests = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById, loading: homesLoading } = useActiveHome();
  const location = useLocation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [homes, setHomes] = useState<Home[]>([]);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [error, setError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newRequestId, setNewRequestId] = useState<string | null>(null);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [requestNotes, setRequestNotes] = useState<Record<string, ServiceRequestNote[]>>({});
  const [newNote, setNewNote] = useState<Record<string, string>>({});
  const [addingNote, setAddingNote] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<Record<string, ServiceRequestQuote[]>>({});
  const [loadingQuotes, setLoadingQuotes] = useState<Record<string, boolean>>({});
  const [quoteAttachments, setQuoteAttachments] = useState<Record<string, Attachment[]>>({});
  const [serviceRequestAttachments, setServiceRequestAttachments] = useState<Record<string, Attachment[]>>({});
  const [loadingAttachments, setLoadingAttachments] = useState<Record<string, boolean>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [step1View, setStep1View] = useState<'intro' | 'home'>('intro');
  const [formData, setFormData] = useState({
    home_id: '',
    title: '',
    description: '',
    priority: 'normal' as ServiceRequest['priority']
  });
  const [triageLoading, setTriageLoading] = useState(false);
  const [clarifyQuestions, setClarifyQuestions] = useState<ClarifyQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [accumulatedAnswers, setAccumulatedAnswers] = useState<ClarifyQuestion[]>([]);
  const [clarifySubmitting, setClarifySubmitting] = useState(false);
  const [updatedTitle, setUpdatedTitle] = useState<string | null>(null);

  // Use workflow hook
  const {
    workflow,
    initializeWorkflow,
    submitClarifyAnswers,
  } = useServiceRequestWorkflow(newRequestId);

  useEffect(() => {
    fetchRequests();

    const state = location.state as { openNewRequest?: boolean } | null;
    if (state?.openNewRequest) {
      setIsFormOpen(true);
      if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof document !== 'undefined') {
        window.history.replaceState({}, document.title);
      }
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.altKey || e.metaKey) && e.key === 'd') {
          e.preventDefault();
          setShowDebug(prev => !prev);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  useEffect(() => {
    const homeList = (accessibleHomes || []) as Home[];
    setHomes(homeList);
    setFormData((prev) => {
      // Keep existing selection if it's still valid
      if (prev.home_id && homeList.some((h) => h.id === prev.home_id)) {
        return prev;
      }
      // Auto-select the only home if there's exactly one
      if (homeList.length === 1) {
        return { ...prev, home_id: homeList[0].id };
      }
      // Fall back to active home if it exists in the list
      if (activeHome?.id && homeList.some((h) => h.id === activeHome.id)) {
        return { ...prev, home_id: activeHome.id };
      }
      // No valid selection
      return { ...prev, home_id: '' };
    });
  }, [accessibleHomes, activeHome, homesLoading]);

  // Fetch workflow when newRequestId is set
  useEffect(() => {
    if (newRequestId && !workflow) {
      const fetchWorkflowData = async () => {
        try {
          // Try to fetch existing workflow first
          const { data: existingWorkflow, error: fetchError } = await supabase
            .from('service_request_agent_workflow')
            .select('*')
            .eq('service_request_id', newRequestId)
            .maybeSingle();
          
          if (fetchError) {
            console.error('Error fetching workflow:', fetchError);
            return;
          }
          
          if (!existingWorkflow) {
            await initializeWorkflow();
          }
        } catch (error) {
          console.error('Error initializing workflow:', error);
        }
      };
      fetchWorkflowData();
    }
  }, [newRequestId, workflow, initializeWorkflow]);

  const fetchRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setRequests([]);
        return;
      }
      const homeIds = await getAccessibleHomeIds(user.id);
      const userHomes = homeIds.map((id) => ({ id }));

      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          home:homes (
            address,
            city,
            state
          )
        `)
        .in('home_id', userHomes.map((h) => h.id))
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === 'PGRST116') {
          setError('You do not have permission to view these service requests.');
          setRequests([]);
          return;
        }
        throw error;
      }

      setRequests(data || []);
      setError('');
    } catch (error: any) {
      console.error('Error fetching requests:', error);
      setError(error.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    if (!formData.home_id) {
      setError('Please select a home for this service request.');
      setSubmitting(false);
      return;
    }

    try {
      // Auto-generate title with current date
      const today = new Date();
      const dateString = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const autoTitle = `Service Request - ${dateString}`;

      const { data, error } = await supabase
        .from('service_requests')
        .insert([{
          ...formData,
          title: autoTitle,
          home_id: formData.home_id
        }])
        .select()
        .single();

      if (error) throw error;

      const requestId = data.id;
      setNewRequestId(requestId);

      // Initialize workflow - must complete before triage
      try {
        await initializeWorkflow();
        // Wait a moment to ensure workflow is saved
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (workflowError: any) {
        console.error('Error initializing workflow:', workflowError);
        setError(`Failed to initialize workflow: ${workflowError.message}`);
        setSubmitting(false);
        return; // Don't proceed if workflow initialization fails
      }

      // Send email notification (only if one exists)
      try {
        const { data: notifications, error: notificationError } = await supabase
          .from('email_notifications')
          .select('*')
          .eq('service_request_id', requestId)
          .order('sent_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!notificationError && notifications) {
          await sendServiceRequestEmail(notifications);
        }
      } catch (emailError) {
        console.error('Error sending email:', emailError);
        // Continue even if email fails
      }

      fetchRequests();

      // Move to step 4 (analyzing/clarifying)
      setCurrentStep(4);
      
      // Trigger triage analysis automatically after workflow is initialized
      // Pass requestId directly to avoid state timing issues
      handleTriageAnalysis(requestId);
    } catch (error: any) {
      setError(error.message);
      console.error('Error submitting service request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTriageAnalysis = async (requestId?: string) => {
    const idToUse = requestId || newRequestId;
    
    if (!idToUse) {
      console.error('handleTriageAnalysis: No requestId available');
      return;
    }

    setTriageLoading(true);
    setError('');

    try {
      console.log('Starting triage analysis for request:', idToUse);
      
      // Check if workflow exists in database (don't rely on state)
      const { data: existingWorkflow, error: checkError } = await supabase
        .from('service_request_agent_workflow')
        .select('*')
        .eq('service_request_id', idToUse)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking for existing workflow:', checkError);
        throw checkError;
      }

      // Only initialize if workflow doesn't exist
      if (!existingWorkflow) {
        console.log('Workflow not found, initializing...');
        // Initialize workflow directly with the requestId
        const initialWorkflowData = {
          triage: {
            status: 'pending',
            started_at: null,
            completed_at: null,
            human_intervention: false,
            notes: '',
            substeps: [],
            awaiting_confirmation: false,
          },
          clarify: {
            status: 'pending',
            started_at: null,
            completed_at: null,
            human_intervention: false,
            notes: '',
            substeps: [],
            awaiting_confirmation: false,
          },
          plan: { status: 'pending', started_at: null, completed_at: null, human_intervention: false, notes: '', substeps: [], awaiting_confirmation: false },
          outreach: { status: 'pending', started_at: null, completed_at: null, human_intervention: false, notes: '', substeps: [], awaiting_confirmation: false },
          analyze: { status: 'pending', started_at: null, completed_at: null, human_intervention: false, notes: '', substeps: [], awaiting_confirmation: false },
          schedule: { status: 'pending', started_at: null, completed_at: null, human_intervention: false, notes: '', substeps: [], awaiting_confirmation: false },
        };

        const { error: initError } = await supabase
          .from('service_request_agent_workflow')
          .insert({
            service_request_id: idToUse,
            current_stage: 'triage',
            workflow_data: initialWorkflowData,
          });

        if (initError) {
          // If it's a duplicate key error, workflow was created between check and insert
          if (initError.code === '23505') {
            console.log('Workflow was created by another process, continuing...');
          } else {
            console.error('Error initializing workflow:', initError);
            throw initError;
          }
        }
        
        // Wait a moment for workflow to be saved
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.log('Workflow already exists, proceeding with triage...');
      }

      // Call the edge function directly with the requestId to avoid hook state issues
      console.log('Calling analyze-service-request edge function...');
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/analyze-service-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            service_request_id: idToUse,
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to analyze service request';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          
          // Check for OpenAI API key errors
          if (errorMessage.includes('API key') || errorMessage.includes('401')) {
            errorMessage = 'Service temporarily unavailable. Please try again later or contact support.';
            console.error('OpenAI API key issue detected in edge function');
          }
        } catch (parseError) {
          // If we can't parse the error, use the status text
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Triage analysis result:', result);
      
      // Wait a moment for workflow to update in the database
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Fetch workflow directly to get updated clarifying questions
      const { data: workflowData, error: fetchError } = await supabase
        .from('service_request_agent_workflow')
        .select('*')
        .eq('service_request_id', idToUse)
        .maybeSingle();
      
      if (fetchError) {
        console.error('Error fetching workflow after triage:', fetchError);
        throw fetchError;
      }
      
      if (!workflowData) {
        console.error('No workflow data found after triage');
        throw new Error('Workflow not found after triage analysis');
      }
      
      console.log('Workflow data after triage:', JSON.stringify(workflowData, null, 2));
      
      // Check if there are clarifying questions
      const questions = workflowData?.workflow_data?.clarify?.clarifying_questions || [];
      
      console.log('Clarifying questions found:', questions.length);
      console.log('Questions data:', JSON.stringify(questions, null, 2));
      
      if (questions.length > 0) {
        // Convert to ClarifyQuestion format
        const formattedQuestions: ClarifyQuestion[] = questions.map((q: any, index: number) => ({
          id: q.id || `question-${index}`,
          question: q.question,
          answer: q.answer || '',
        }));
        console.log('Formatted questions:', formattedQuestions);
        setClarifyQuestions(formattedQuestions);
        setCurrentQuestionIndex(0);
        setAccumulatedAnswers([]);
        // Stay on step 4 to show questions
      } else {
        console.log('No clarifying questions, moving to confirmation');
        // No questions needed, move to confirmation
        setCurrentStep(5);
      }
    } catch (error: any) {
      console.error('Error in triage analysis:', error);
      setError(`Failed to analyze your request: ${error.message}. We'll review it manually.`);
      // Move to confirmation even on error
      setCurrentStep(5);
    } finally {
      setTriageLoading(false);
    }
  };

  const handleSingleQuestionSubmit = async (answer: ClarifyQuestion) => {
    if (!newRequestId) return;

    setClarifySubmitting(true);
    setError('');

    try {
      // Add current answer to accumulated answers
      const updatedAccumulatedAnswers = [...accumulatedAnswers, answer];
      setAccumulatedAnswers(updatedAccumulatedAnswers);

      // Check if there are more questions to answer
      if (currentQuestionIndex + 1 < clarifyQuestions.length) {
        // Move to next question
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setClarifySubmitting(false);
      } else {
        // All questions answered, submit them all
        const result = await submitClarifyAnswers(updatedAccumulatedAnswers);
        
        // Wait a moment for workflow to update
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch updated workflow to get new questions if any
        const { data: workflowData, error: workflowFetchError } = await supabase
          .from('service_request_agent_workflow')
          .select('*')
          .eq('service_request_id', newRequestId)
          .maybeSingle();
        
        if (workflowFetchError) {
          console.error('Error fetching workflow after clarify:', workflowFetchError);
          throw workflowFetchError;
        }
        
        if (!workflowData) {
          console.error('No workflow data found after clarify');
          throw new Error('Workflow not found after processing answers');
        }
        
        if (result.sufficient_information) {
          // Update title if provided
          if (result.new_title) {
            setUpdatedTitle(result.new_title);
          }
          
          // Move to confirmation
          setCurrentStep(5);
          // Reset question state
          setCurrentQuestionIndex(0);
          setAccumulatedAnswers([]);
        } else {
          // More questions needed - get them from workflow
          const newQuestions = workflowData?.workflow_data?.clarify?.clarifying_questions || [];
          const unansweredQuestions = newQuestions.filter((q: any) => !q.answer || q.answer.trim() === '');
          
          if (unansweredQuestions.length > 0) {
            const formattedQuestions: ClarifyQuestion[] = unansweredQuestions.map((q: any) => ({
              id: q.id,
              question: q.question,
              answer: q.answer || '',
            }));
            setClarifyQuestions(formattedQuestions);
            setCurrentQuestionIndex(0);
            setAccumulatedAnswers([]);
            // Stay on step 4 to answer new questions
          } else {
            // No more questions but not sufficient - move to confirmation anyway
            setCurrentStep(5);
            setCurrentQuestionIndex(0);
            setAccumulatedAnswers([]);
          }
        }
      }
    } catch (error: any) {
      setError(`Failed to process your answers: ${error.message}. Please try again.`);
      console.error('Error submitting clarify answers:', error);
    } finally {
      setClarifySubmitting(false);
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setCurrentStep(1);
    setStep1View('intro');
    setNewRequestId(null);
    // Default the home back to a sensible value: only home if there's just one,
    // otherwise the active home, otherwise blank so the user must pick.
    const defaultHomeId =
      homes.length === 1
        ? homes[0].id
        : activeHome?.id && homes.some((h) => h.id === activeHome.id)
        ? activeHome.id
        : '';
    setFormData({
      home_id: defaultHomeId,
      title: '',
      description: '',
      priority: 'normal'
    });
    setError('');
    setTriageLoading(false);
    setClarifyQuestions([]);
    setCurrentQuestionIndex(0);
    setAccumulatedAnswers([]);
    setClarifySubmitting(false);
    setUpdatedTitle(null);
  };

  const handleNextStep = async () => {
    if (currentStep === 3) {
      // Step 3 -> Step 4: Create service request and start triage
      await handleSubmit();
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    // Don't allow going back from step 4 onwards (after service request is created)
    if (currentStep > 1 && currentStep < 4) {
      // From step 2 (description), go back to step 1's home selector view
      // so the user can change the home they picked.
      if (currentStep === 2) {
        setCurrentStep(1);
        setStep1View('home');
        return;
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedFromStep = (step: number): boolean => {
    switch (step) {
      case 1:
        // Must have selected a home before describing the request.
        return !!formData.home_id;
      case 2:
        return formData.description.trim().length > 0;
      case 3:
        return !!formData.home_id;
      default:
        return false;
    }
  };

  const handleTestEmail = async () => {
    setSendingTest(true);
    setError('');

    try {
      const { data: notification, error: notificationError } = await supabase
        .from('email_notifications')
        .insert([{
          recipient: 'service@honeydone.com',
          subject: 'Test Email from HoneyDone',
          body: 'This is a test email from the HoneyDone service request system.'
        }])
        .select()
        .single();

      if (notificationError) throw notificationError;

      await sendServiceRequestEmail(notification);
      Alert.alert('Success', 'Test email sent successfully to service@honeydone.com');
    } catch (error: any) {
      setError('Failed to send test email: ' + error.message);
      console.error('Error sending test email:', error);
    } finally {
      setSendingTest(false);
    }
  };

  const handleDeleteRequest = async (id: string) => {
    Alert.alert(
      'Delete Service Request',
      'Are you sure you want to delete this service request?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setDeleting(true);
            try {
              const { error } = await supabase
                .from('service_requests')
                .delete()
                .eq('id', id);

              if (error) throw error;
              fetchRequests();
            } catch (error: any) {
              console.error('Error deleting request:', error);
              Alert.alert('Error', 'Failed to delete request: ' + error.message);
            } finally {
              setDeleting(false);
            }
          }
        }
      ]
    );
  };

  const fetchNotes = async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('service_request_notes')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setRequestNotes(prev => ({
        ...prev,
        [requestId]: data || []
      }));
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleAddNote = async (requestId: string) => {
    const noteContent = newNote[requestId]?.trim();
    if (!noteContent) return;

    setAddingNote(requestId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('service_request_notes')
        .insert([{
          service_request_id: requestId,
          content: noteContent,
          created_by: user?.id
        }])
        .select()
        .single();

      if (error) throw error;

      setRequestNotes(prev => ({
        ...prev,
        [requestId]: [data, ...(prev[requestId] || [])]
      }));
      
      setNewNote(prev => ({
        ...prev,
        [requestId]: ''
      }));
    } catch (error) {
      console.error('Error adding note:', error);
      Alert.alert('Error', 'Failed to add note. Please try again.');
    } finally {
      setAddingNote(null);
    }
  };

  const handleDeleteNote = async (requestId: string, noteId: string) => {
    Alert.alert(
      'Delete Note',
      'Are you sure you want to delete this note?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setDeletingNoteId(noteId);
            try {
              const { error } = await supabase
                .from('service_request_notes')
                .delete()
                .eq('id', noteId);

              if (error) throw error;

              setRequestNotes(prev => ({
                ...prev,
                [requestId]: (prev[requestId] || []).filter(note => note.id !== noteId)
              }));
            } catch (error) {
              console.error('Error deleting note:', error);
              Alert.alert('Error', 'Failed to delete note. Please try again.');
            } finally {
              setDeletingNoteId(null);
            }
          }
        }
      ]
    );
  };

  const handleExpandRequest = async (requestId: string) => {
    const newExpandedId = expandedRequestId === requestId ? null : requestId;
    setExpandedRequestId(newExpandedId);
    
    if (newExpandedId) {
      if (!requestNotes[requestId]) {
        await fetchNotes(requestId);
      }
      if (!quotes[requestId]) {
        await fetchQuotes(requestId);
      }
      if (!serviceRequestAttachments[requestId]) {
        await fetchAttachments(requestId);
      }
    }
  };

  const fetchQuotes = async (requestId: string) => {
    try {
      setLoadingQuotes(prev => ({ ...prev, [requestId]: true }));
      const { data, error } = await supabase
        .from('service_request_quotes')
        .select(`
          *,
          service_provider:service_providers (
            id,
            company_name,
            email,
            phone,
            address
          )
        `)
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data and fetch attachments
      const attachmentsMap: Record<string, Attachment[]> = {};
      const transformedQuotes = await Promise.all(
        (data || []).map(async (quote: any) => {
          // Fetch full attachment data for each quote
          const { data: attachmentsData, error: attachmentsError } = await supabase
            .from('service_request_quote_attachments')
            .select('*')
            .eq('quote_id', quote.id)
            .order('created_at', { ascending: false });

          const attachments = attachmentsError ? [] : (attachmentsData || []);
          attachmentsMap[quote.id] = attachments;

          return {
            ...quote,
            service_provider: Array.isArray(quote.service_provider) 
              ? quote.service_provider[0] 
              : quote.service_provider
          };
        })
      );

      setQuotes(prev => ({ ...prev, [requestId]: transformedQuotes }));
      setQuoteAttachments(prev => ({ ...prev, ...attachmentsMap }));
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes(prev => ({ ...prev, [requestId]: [] }));
    } finally {
      setLoadingQuotes(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const fetchAttachments = async (requestId: string) => {
    try {
      setLoadingAttachments(prev => ({ ...prev, [requestId]: true }));
      const { data, error } = await supabase
        .from('service_request_attachments')
        .select('*')
        .eq('service_request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceRequestAttachments(prev => ({ ...prev, [requestId]: data || [] }));
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setServiceRequestAttachments(prev => ({ ...prev, [requestId]: [] }));
    } finally {
      setLoadingAttachments(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const getQuoteStatusColor = (status: ServiceRequestQuote['status']) => {
    switch (status) {
      case 'accepted': return { text: '#16a34a', bg: '#dcfce7' };
      case 'rejected': return { text: '#dc2626', bg: '#fee2e2' };
      default: return { text: '#ca8a04', bg: '#fef9c3' };
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon size={16} color="#6b7280" />;
    if (fileType.startsWith('video/')) return <Video size={16} color="#6b7280" />;
    if (fileType === 'application/pdf') return <FileText size={16} color="#6b7280" />;
    return <File size={16} color="#6b7280" />;
  };

  const isImageFile = (fileType: string) => fileType.startsWith('image/');

  const downloadAttachment = async (attachment: Attachment, bucket: 'service-request-attachments' | 'service-request-quote-attachments') => {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(attachment.file_url);

    await downloadFile(publicUrl, attachment.file_name);
  };

  const viewImage = (attachment: Attachment, bucket: 'service-request-attachments' | 'service-request-quote-attachments') => {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(attachment.file_url);
    setPreviewImage(publicUrl);
  };


  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return { text: '#16a34a', bg: '#f0fdf4' };
      case 'in_progress': return { text: '#2563eb', bg: '#eff6ff' };
      case 'cancelled': return { text: '#4b5563', bg: '#f9fafb' };
      default: return { text: '#ea580c', bg: '#fff7ed' };
    }
  };

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text style={styles.title}>Service Requests</Text>
          <Text style={styles.subtitle}>Submit and track your service requests</Text>
        </View>
        <View style={styles.headerActions}>
          <Pressable
            onPress={() => setIsFormOpen(true)}
            style={styles.newRequestButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.newRequestText}>New Request</Text>
          </Pressable>
          {showDebug && (
            <View style={styles.debugContainer}>
              {Platform.OS === 'web' && (
                <Bug size={16} color="#dc2626" />
              )}
              <View style={styles.debugActions}>
                <Pressable
                  onPress={handleTestEmail}
                  disabled={sendingTest}
                  style={[styles.debugButton, sendingTest && styles.debugButtonDisabled]}
                >
                  {sendingTest ? (
                    <View style={styles.debugButtonContent}>
                      <ActivityIndicator size="small" color="#dc2626" />
                      <Text style={styles.debugButtonText}>Sending...</Text>
                    </View>
                  ) : (
                    <View style={styles.debugButtonContent}>
                      <Send size={16} color="#dc2626" />
                      <Text style={styles.debugButtonText}>Test Email</Text>
                    </View>
                  )}
                </Pressable>
                <Pressable
                  onPress={() => requests.forEach(r => handleDeleteRequest(r.id))}
                  disabled={deleting || requests.length === 0}
                  style={[styles.debugButton, (deleting || requests.length === 0) && styles.debugButtonDisabled]}
                >
                  {deleting ? (
                    <View style={styles.debugButtonContent}>
                      <ActivityIndicator size="small" color="#dc2626" />
                      <Text style={styles.debugButtonText}>Deleting...</Text>
                    </View>
                  ) : (
                    <View style={styles.debugButtonContent}>
                      <Trash2 size={16} color="#dc2626" />
                      <Text style={styles.debugButtonText}>Delete All</Text>
                    </View>
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      ) : requests.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No service requests yet</Text>
        </View>
      ) : (
        <View style={styles.requestsContainer}>
          {requests.map((request) => {
            const statusColors = getStatusColor(request.status);
            return (
              <View key={request.id} style={styles.requestItem}>
                <View style={styles.requestContent}>
                  <View style={styles.requestHeader}>
                    <View style={styles.requestInfo}>
                      <Text style={styles.requestTitle}>{request.title}</Text>
                      <Text style={styles.requestDescription}>{request.description}</Text>
                      <View style={styles.requestTags}>
                        <View style={[styles.tag, { backgroundColor: statusColors.bg }]}>
                          <Text style={[styles.tagText, { color: statusColors.text }]}>
                            {request.status.split('_').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Text>
                        </View>
                        <Pressable
                          onPress={() => handleExpandRequest(request.id)}
                          style={styles.detailsButton}
                        >
                          <MessageSquare size={16} color="#2563eb" />
                          <Text style={styles.detailsButtonText}>Details</Text>
                          {expandedRequestId === request.id ? (
                            <ChevronUp size={16} color="#2563eb" />
                          ) : (
                            <ChevronDown size={16} color="#2563eb" />
                          )}
                        </Pressable>
                        {showDebug && (
                          <Pressable
                            onPress={() => handleDeleteRequest(request.id)}
                            disabled={deleting}
                            style={styles.deleteButton}
                          >
                            <Trash2 size={16} color="#dc2626" />
                            <Text style={styles.deleteButtonText}>Delete</Text>
                          </Pressable>
                        )}
                      </View>
                    </View>
                    <Text style={styles.requestDate}>
                      {new Date(request.created_at).toLocaleDateString()}
                    </Text>
                  </View>

                  {expandedRequestId === request.id && (
                    <View style={styles.expandedContent}>
                      <View style={styles.notesSection}>
                        <View style={styles.sectionHeader}>
                          <MessageSquare size={20} color="#6b7280" />
                          <Text style={styles.sectionTitle}>Notes & Comments</Text>
                        </View>
                        
                        <View style={styles.addNoteContainer}>
                          <TextInput
                            style={styles.noteInput}
                            value={newNote[request.id] || ''}
                            onChangeText={(value) => setNewNote(prev => ({ ...prev, [request.id]: value }))}
                            placeholder="Add a note or comment..."
                            multiline
                            numberOfLines={2}
                            placeholderTextColor="#9ca3af"
                          />
                          <Pressable
                            onPress={() => handleAddNote(request.id)}
                            disabled={!newNote[request.id]?.trim() || addingNote === request.id}
                            style={[styles.addNoteButton, (!newNote[request.id]?.trim() || addingNote === request.id) && styles.addNoteButtonDisabled]}
                          >
                            {addingNote === request.id ? (
                              <ActivityIndicator size="small" color="#ffffff" />
                            ) : (
                              <Text style={styles.addNoteButtonText}>Add</Text>
                            )}
                          </Pressable>
                        </View>
                        
                        <View style={styles.notesList}>
                          {requestNotes[request.id]?.length === 0 ? (
                            <Text style={styles.noNotesText}>No notes yet</Text>
                          ) : (
                            requestNotes[request.id]?.map((note) => (
                              <View key={note.id} style={styles.noteItem}>
                                <View style={styles.noteContent}>
                                  <Text style={styles.noteText}>{note.content}</Text>
                                  <Pressable
                                    onPress={() => handleDeleteNote(request.id, note.id)}
                                    disabled={deletingNoteId === note.id}
                                    style={styles.deleteNoteButton}
                                  >
                                    {deletingNoteId === note.id ? (
                                      <ActivityIndicator size="small" color="#9ca3af" />
                                    ) : (
                                      <Trash2 size={16} color="#9ca3af" />
                                    )}
                                  </Pressable>
                                </View>
                                <Text style={styles.noteDate}>
                                  {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                                </Text>
                              </View>
                            ))
                          )}
                        </View>
                      </View>

                      {/* Quotes Section */}
                      <View style={styles.quotesSection}>
                        <View style={styles.sectionHeader}>
                          <DollarSign size={20} color="#6b7280" />
                          <Text style={styles.sectionTitle}>Quotes/Estimates</Text>
                        </View>
                        {loadingQuotes[request.id] ? (
                          <View style={styles.loadingContainer}>
                            <ActivityIndicator size="small" color="#9ca3af" />
                          </View>
                        ) : !quotes[request.id] || quotes[request.id].length === 0 ? (
                          <Text style={styles.noNotesText}>No quotes yet</Text>
                        ) : (
                          <View style={styles.quotesList}>
                            {quotes[request.id].map((quote) => {
                              const quoteStatusColors = getQuoteStatusColor(quote.status);
                              return (
                                <View key={quote.id} style={styles.quoteCard}>
                                  <View style={styles.quoteHeader}>
                                    <View style={styles.quoteInfo}>
                                      <Text style={styles.quoteProviderName}>
                                        {quote.service_provider?.company_name || 'Unknown Provider'}
                                      </Text>
                                      <View style={[styles.tag, { backgroundColor: quoteStatusColors.bg }]}>
                                        <Text style={[styles.tagText, { color: quoteStatusColors.text }]}>
                                          {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                                        </Text>
                                      </View>
                                    </View>
                                    {quote.amount !== null && quote.amount !== undefined && (
                                      <Text style={styles.quoteAmount}>{formatCurrency(quote.amount)}</Text>
                                    )}
                                  </View>
                                  {quote.notes && (
                                    <Text style={styles.quoteNotes}>{quote.notes}</Text>
                                  )}
                                  <View style={styles.quoteMeta}>
                                    {quote.quote_date && (
                                      <View style={styles.detailRow}>
                                        <Calendar size={12} color="#6b7280" />
                                        <Text style={styles.quoteMetaText}>
                                          Quote Date: {new Date(quote.quote_date).toLocaleDateString()}
                                        </Text>
                                      </View>
                                    )}
                                    {quote.expires_at && (
                                      <View style={styles.detailRow}>
                                        <Clock size={12} color="#6b7280" />
                                        <Text style={styles.quoteMetaText}>
                                          Expires: {new Date(quote.expires_at).toLocaleDateString()}
                                        </Text>
                                      </View>
                                    )}
                                  </View>
                                  {/* Quote Attachments */}
                                  {quoteAttachments[quote.id] && quoteAttachments[quote.id].length > 0 && (
                                    <View style={styles.attachmentsContainer}>
                                      {quoteAttachments[quote.id].map((attachment) => (
                                        <Pressable
                                          key={attachment.id}
                                          style={styles.attachmentItem}
                                          onPress={() => downloadAttachment(attachment, 'service-request-quote-attachments')}
                                        >
                                          <View style={styles.attachmentContent}>
                                            {getFileIcon(attachment.file_type)}
                                            <View style={styles.attachmentInfo}>
                                              <Text style={styles.attachmentName} numberOfLines={1}>
                                                {attachment.file_name}
                                              </Text>
                                              <Text style={styles.attachmentSize}>
                                                {formatFileSize(attachment.file_size)}
                                              </Text>
                                            </View>
                                          </View>
                                          <View style={styles.attachmentActions}>
                                            {isImageFile(attachment.file_type) && (
                                              <Pressable
                                                style={styles.attachmentAction}
                                                onPress={(e) => {
                                                  e.stopPropagation();
                                                  viewImage(attachment, 'service-request-quote-attachments');
                                                }}
                                              >
                                                <Eye size={14} color="#6b7280" />
                                              </Pressable>
                                            )}
                                            <Pressable
                                              style={styles.attachmentAction}
                                              onPress={(e) => {
                                                e.stopPropagation();
                                                downloadAttachment(attachment, 'service-request-quote-attachments');
                                              }}
                                            >
                                              <Download size={14} color="#6b7280" />
                                            </Pressable>
                                          </View>
                                        </Pressable>
                                      ))}
                                    </View>
                                  )}
                                  <Text style={styles.quoteCreated}>
                                    Created: {new Date(quote.created_at).toLocaleDateString()} at {new Date(quote.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </Text>
                                </View>
                              );
                            })}
                          </View>
                        )}
                      </View>

                      <View style={styles.attachmentsSection}>
                        <View style={styles.sectionHeader}>
                          <Paperclip size={20} color="#6b7280" />
                          <Text style={styles.sectionTitle}>Attachments</Text>
                        </View>
                        {loadingAttachments[request.id] ? (
                          <View style={styles.loadingContainer}>
                            <ActivityIndicator size="small" color="#9ca3af" />
                          </View>
                        ) : serviceRequestAttachments[request.id] && serviceRequestAttachments[request.id].length > 0 ? (
                          <View style={styles.attachmentsContainer}>
                            {serviceRequestAttachments[request.id].map((attachment) => (
                              <Pressable
                                key={attachment.id}
                                style={styles.attachmentItem}
                                onPress={() => downloadAttachment(attachment, 'service-request-attachments')}
                              >
                                <View style={styles.attachmentContent}>
                                  {getFileIcon(attachment.file_type)}
                                  <View style={styles.attachmentInfo}>
                                    <Text style={styles.attachmentName} numberOfLines={1}>
                                      {attachment.file_name}
                                    </Text>
                                    <Text style={styles.attachmentSize}>
                                      {formatFileSize(attachment.file_size)}
                                    </Text>
                                  </View>
                                </View>
                                <View style={styles.attachmentActions}>
                                  {isImageFile(attachment.file_type) && (
                                    <Pressable
                                      style={styles.attachmentAction}
                                      onPress={(e) => {
                                        e.stopPropagation();
                                        viewImage(attachment, 'service-request-attachments');
                                      }}
                                    >
                                      <Eye size={14} color="#6b7280" />
                                    </Pressable>
                                  )}
                                  <Pressable
                                    style={styles.attachmentAction}
                                    onPress={(e) => {
                                      e.stopPropagation();
                                      downloadAttachment(attachment, 'service-request-attachments');
                                    }}
                                  >
                                    <Download size={14} color="#6b7280" />
                                  </Pressable>
                                </View>
                              </Pressable>
                            ))}
                          </View>
                        ) : null}
                        <ServiceRequestFileUpload
                          serviceRequestId={request.id}
                        />
                      </View>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {/* Image Preview Modal */}
      <Modal
        visible={!!previewImage}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewImage(null)}
      >
        <TouchableWithoutFeedback onPress={() => setPreviewImage(null)}>
          <View style={styles.imagePreviewOverlay}>
            <Pressable
              style={styles.imagePreviewClose}
              onPress={() => setPreviewImage(null)}
            >
              <Text style={styles.imagePreviewCloseText}>✕</Text>
            </Pressable>
            {previewImage && (
              <Image
                source={{ uri: previewImage }}
                style={styles.imagePreview as any}
                resizeMode="contain"
              />
            )}
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      <BottomSheet
        isOpen={isFormOpen}
        onClose={(submitting || triageLoading || clarifySubmitting) ? () => {} : handleCloseForm}
        title={currentStep === 5 ? 'Request Submitted!' : 'New Service Request'}
        height="full"
      >
        {/* Step Indicator */}
        {currentStep !== 5 && (
          <View style={styles.stepIndicator}>
            {[1, 2, 3, 4, 5].map((step) => (
              <React.Fragment key={step}>
              <View style={styles.stepCircle}>
                {currentStep > step ? (
                  <CheckCircle2 size={20} color="#16a34a" />
                ) : (
                    <View style={[
                      styles.stepNumber,
                      currentStep === step && styles.stepNumberActive
                    ]}>
                      <Text style={[
                        styles.stepNumberText,
                        currentStep === step && styles.stepNumberTextActive
                      ]}>
                        {step}
                      </Text>
                    </View>
                  )}
                </View>
                {step < 5 && (
                  <View style={[
                    styles.stepLine,
                    currentStep > step && styles.stepLineActive
                  ]} />
                )}
              </React.Fragment>
            ))}
          </View>
        )}

        {/* Step 1: Introduction */}
        {currentStep === 1 && step1View === 'intro' && (
          <View style={styles.stepContent}>
            <View style={styles.introContainer}>
              <Text style={styles.introTitle}>How Service Requests Work</Text>
              <View style={styles.introFeature}>
                <MessageSquare size={24} color="#2563eb" />
                <View style={styles.introFeatureText}>
                  <Text style={styles.introFeatureTitle}>Submit Your Request</Text>
                  <Text style={styles.introFeatureDescription}>
                    Tell us what service you need. We'll review your request and coordinate with our team.
                  </Text>
                </View>
              </View>
              <View style={styles.introFeature}>
                <Clock size={24} color="#2563eb" />
                <View style={styles.introFeatureText}>
                  <Text style={styles.introFeatureTitle}>Quick Response</Text>
                  <Text style={styles.introFeatureDescription}>
                    We'll contact you within 24 hours to discuss next steps and schedule a visit if needed.
                  </Text>
                </View>
              </View>
              <View style={styles.introFeature}>
                <DollarSign size={24} color="#2563eb" />
                <View style={styles.introFeatureText}>
                  <Text style={styles.introFeatureTitle}>Transparent Quotes</Text>
                  <Text style={styles.introFeatureDescription}>
                    Receive detailed quotes before work begins. You'll always know what to expect.
                  </Text>
                </View>
              </View>
            </View>
            <View style={styles.stepActions}>
              <Pressable
                onPress={() => setStep1View('home')}
                style={styles.primaryButton}
              >
                <Text style={styles.primaryButtonText}>Get Started</Text>
                <ChevronRight size={20} color="#ffffff" />
              </Pressable>
            </View>
          </View>
        )}

        {/* Step 1: Home Selection */}
        {currentStep === 1 && step1View === 'home' && (
          <View style={styles.stepContent}>
            <View>
              <Text style={styles.stepTitle}>
                {homes.length > 1 ? 'Which home is this for?' : 'Confirm your home'}
              </Text>
              <Text style={styles.stepSubtitle}>
                {homes.length > 1
                  ? 'Tap the home this service request is for.'
                  : 'Tap your home below to continue.'}
              </Text>
            </View>

            <View style={styles.homeCardsContainer}>
              {homesLoading ? (
                <View style={styles.homeLoadingRow}>
                  <ActivityIndicator size="small" color="#9ca3af" />
                  <Text style={styles.homeLoadingText}>Loading your homes...</Text>
                </View>
              ) : homes.length === 0 ? (
                <View style={styles.errorContainer}>
                  <AlertCircle size={16} color="#DC2626" />
                  <Text style={styles.errorText}>
                    You don't have a home set up yet. Please add a home before creating a service request.
                  </Text>
                </View>
              ) : (
                homes.map((home) => {
                  const isSelected = formData.home_id === home.id;
                  return (
                    <Pressable
                      key={home.id}
                      onPress={() => {
                        setFormData({ ...formData, home_id: home.id });
                        if (homes.length > 1) {
                          setActiveHomeById(home.id);
                        }
                        setCurrentStep(2);
                      }}
                      style={[
                        styles.homeCard,
                        isSelected && styles.homeCardSelected
                      ]}
                    >
                      <View style={styles.homeCardContent}>
                        <Text style={styles.homeCardAddress}>{home.address}</Text>
                        <Text style={styles.homeCardLocation}>
                          {home.city}, {home.state}
                        </Text>
                      </View>
                      <ChevronRight size={20} color="#9ca3af" />
                    </Pressable>
                  );
                })
              )}
            </View>

            <View style={styles.stepActions}>
              <Pressable
                onPress={() => setStep1View('intro')}
                style={styles.secondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Back</Text>
              </Pressable>
            </View>
          </View>
        )}

        {/* Step 2: Description */}
        {currentStep === 2 && (
          <View style={styles.stepContent}>
            <View>
              <Text style={styles.stepTitle}>Tell us about your service need</Text>
              <Text style={styles.stepSubtitle}>
                Describe what you need help with. Be as detailed as possible so we can best assist you.
              </Text>
            </View>
            <View style={styles.form}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>What do you need help with? *</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.description}
                  onChangeText={(value) => setFormData({ ...formData, description: value })}
                  placeholder="Describe the issue, location, and any relevant details. For example: 'The kitchen faucet has been leaking for a few days. Water drips from the base when turned on. It's located under the window above the sink.'"
                  multiline
                  numberOfLines={8}
                  placeholderTextColor="#9ca3af"
                />
              </View>
            </View>
            {error ? (
              <View>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
            <View style={styles.stepActions}>
              <Pressable
                onPress={handlePreviousStep}
                style={styles.secondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Back</Text>
              </Pressable>
              <Pressable
                onPress={handleNextStep}
                disabled={!canProceedFromStep(2)}
                style={[
                  styles.primaryButton,
                  !canProceedFromStep(2) && styles.primaryButtonDisabled
                ]}
              >
                <Text style={styles.primaryButtonText}>Continue</Text>
                <ChevronRight size={20} color="#ffffff" />
              </Pressable>
            </View>
          </View>
        )}

        {/* Step 3: Priority and Home */}
        {currentStep === 3 && (
          <View style={styles.stepContent}>
            <View>
              <Text style={styles.stepTitle}>How urgent is this?</Text>
              <Text style={styles.stepSubtitle}>
                Select the urgency level that best matches your service need. This helps us prioritize and schedule your request appropriately.
              </Text>
            </View>
            <View style={styles.form}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Priority Level</Text>
                <View style={styles.priorityOptions}>
                  {priorities.map((priority) => (
                    <Pressable
                      key={priority.value}
                      onPress={() => setFormData({ ...formData, priority: priority.value as ServiceRequest['priority'] })}
                      style={[
                        styles.priorityOption,
                        formData.priority === priority.value && styles.priorityOptionSelected
                      ]}
                    >
                      <View style={styles.priorityOptionContent}>
                        <View style={styles.priorityOptionHeader}>
                          <Text style={[
                            styles.priorityOptionLabel,
                            formData.priority === priority.value && styles.priorityOptionLabelSelected
                          ]}>
                            {priority.label}
                          </Text>
                          {formData.priority === priority.value && (
                            <CheckCircle2 size={20} color="#000000" />
                          )}
                        </View>
                        <Text style={[
                          styles.priorityOptionDescription,
                          formData.priority === priority.value && styles.priorityOptionDescriptionSelected
                        ]}>
                          {priority.description}
                        </Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              </View>

            </View>

            {error ? (
              <View>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <View style={styles.stepActions}>
              <Pressable
                onPress={handlePreviousStep}
                style={styles.secondaryButton}
              >
                <Text style={styles.secondaryButtonText}>Back</Text>
              </Pressable>
              <Pressable
                onPress={handleNextStep}
                disabled={submitting || !formData.description.trim()}
                style={[
                  styles.primaryButton,
                  (submitting || !formData.description.trim()) && styles.primaryButtonDisabled
                ]}
              >
                {submitting ? (
                  <>
                    <ActivityIndicator size="small" color="#ffffff" />
                    <Text style={styles.primaryButtonText}>Submitting...</Text>
                  </>
                ) : (
                  <>
                    <Text style={styles.primaryButtonText}>Submit Request</Text>
                    <ChevronRight size={20} color="#ffffff" />
                  </>
                )}
              </Pressable>
            </View>
          </View>
        )}

        {/* Step 4: Analyzing & Clarifying Questions */}
        {currentStep === 4 && (
          <View style={styles.stepContent}>
            {triageLoading ? (
              // Show loading state while analyzing
              <View style={styles.confirmationContainer}>
                <View style={styles.confirmationIcon}>
                  <ActivityIndicator size={64} color="#2563eb" />
                </View>
                <Text style={styles.confirmationTitle}>Analyzing Your Request...</Text>
                <Text style={styles.confirmationText}>
                  Our AI is reviewing your request to understand what you need and determine if we need any additional information.
                </Text>
              </View>
            ) : clarifyQuestions.length > 0 ? (
              // Show one clarifying question at a time
              <>
                <View>
                  <Text style={styles.stepTitle}>A Few Quick Questions</Text>
                  <Text style={styles.stepSubtitle}>
                    {currentQuestionIndex + 1} of {clarifyQuestions.length}
                  </Text>
                </View>
                <View style={styles.form}>
                  <View style={styles.singleQuestionContainer}>
                    <View style={styles.questionHeader}>
                      <AlertCircle size={16} color="#2563EB" />
                      <Text style={styles.questionHeaderText}>
                        Please answer this question to help us serve you better:
                      </Text>
                    </View>
                    
                    <View style={styles.questionBlock}>
                      <Text style={styles.questionText}>
                        {clarifyQuestions[currentQuestionIndex]?.question}
                      </Text>
                      <TextInput
                        style={[
                          styles.answerInput,
                          clarifyQuestions[currentQuestionIndex]?.answer.trim() && styles.answerInputFilled
                        ]}
                        placeholder="Your answer..."
                        value={clarifyQuestions[currentQuestionIndex]?.answer || ''}
                        onChangeText={(text) => {
                          const updatedQuestions = [...clarifyQuestions];
                          updatedQuestions[currentQuestionIndex] = {
                            ...updatedQuestions[currentQuestionIndex],
                            answer: text
                          };
                          setClarifyQuestions(updatedQuestions);
                        }}
                        multiline
                        numberOfLines={4}
                        textAlignVertical="top"
                        editable={!clarifySubmitting}
                      />
                    </View>

                    {error && (
                      <View style={styles.errorContainer}>
                        <AlertCircle size={16} color="#DC2626" />
                        <Text style={styles.errorText}>{error}</Text>
                      </View>
                    )}

                    <View style={styles.questionActions}>
                      {currentQuestionIndex > 0 && (
                        <Pressable
                          onPress={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                          style={styles.secondaryButton}
                          disabled={clarifySubmitting}
                        >
                          <Text style={styles.secondaryButtonText}>Previous</Text>
                        </Pressable>
                      )}
                      <Pressable
                        onPress={() => {
                          const currentAnswer = clarifyQuestions[currentQuestionIndex];
                          if (!currentAnswer?.answer.trim()) {
                            setError('Please provide an answer before continuing.');
                            return;
                          }
                          setError('');
                          handleSingleQuestionSubmit(currentAnswer);
                        }}
                        disabled={clarifySubmitting || !clarifyQuestions[currentQuestionIndex]?.answer.trim()}
                        style={[
                          styles.primaryButton,
                          (!clarifyQuestions[currentQuestionIndex]?.answer.trim() || clarifySubmitting) && styles.primaryButtonDisabled
                        ]}
                      >
                        {clarifySubmitting ? (
                          <>
                            <ActivityIndicator size="small" color="#ffffff" />
                            <Text style={styles.primaryButtonText}>Processing...</Text>
                          </>
                        ) : (
                          <>
                            <Text style={styles.primaryButtonText}>
                              {currentQuestionIndex + 1 === clarifyQuestions.length ? 'Submit' : 'Next'}
                            </Text>
                            <ChevronRight size={20} color="#ffffff" />
                          </>
                        )}
                      </Pressable>
                    </View>
                  </View>
                </View>
              </>
            ) : (
              // If no questions and not loading, something went wrong - show error or move to confirmation
              <View style={styles.confirmationContainer}>
                <View style={styles.confirmationIcon}>
                  <CheckCircle2 size={64} color="#16a34a" />
                </View>
                <Text style={styles.confirmationTitle}>Analysis Complete</Text>
                <Text style={styles.confirmationText}>
                  We've reviewed your request. {error ? 'There was an issue, but we\'ll review it manually.' : 'Moving to confirmation...'}
                </Text>
                {error && (
                  <View style={styles.errorContainer}>
                    <AlertCircle size={16} color="#DC2626" />
                    <Text style={styles.errorText}>{error}</Text>
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {/* Step 5: Confirmation */}
        {currentStep === 5 && (
          <View style={styles.stepContent}>
            <View style={styles.confirmationContainer}>
              <View style={styles.confirmationIcon}>
                <CheckCircle2 size={64} color="#16a34a" />
              </View>
              <Text style={styles.confirmationTitle}>Request Received!</Text>
              <Text style={styles.confirmationText}>
                We've received your service request and will contact you shortly to coordinate next steps.
              </Text>

              {newRequestId ? (
                <View style={styles.attachmentSection}>
                  <Text style={styles.attachmentSectionTitle}>Add Attachments (Optional)</Text>
                  <Text style={styles.attachmentSectionSubtitle}>
                    You can add photos or documents to help us better understand your request.
                  </Text>
                  <ServiceRequestFileUpload
                    serviceRequestId={newRequestId}
                  />
                </View>
              ) : null}
              <Pressable
                onPress={handleCloseForm}
                style={styles.primaryButton}
              >
                <Text style={styles.primaryButtonText}>Done</Text>
              </Pressable>
            </View>
          </View>
        )}
      </BottomSheet>

    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: 32,
    paddingBottom: 16,
  },
  header: {
    gap: 16,
  },
  headerTop: {
    gap: 4,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
  },
  headerActions: {
    gap: 16,
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      alignItems: 'center',
    }),
  },
  newRequestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  newRequestText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  debugContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fef2f2',
    padding: 16,
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      paddingHorizontal: 16,
      paddingVertical: 8,
    }),
  },
  debugActions: {
    gap: 8,
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
    }),
  },
  debugButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#dc2626',
    borderRadius: 8,
  },
  debugButtonDisabled: {
    opacity: 0.5,
  },
  debugButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  debugButtonText: {
    color: '#dc2626',
    fontSize: 14,
    fontWeight: '500',
  },
  loadingContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
  },
  requestsContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  requestItem: {
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  requestContent: {
    padding: 24,
  },
  requestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  requestInfo: {
    flex: 1,
    gap: 8,
  },
  requestTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  requestDescription: {
    fontSize: 14,
    color: '#4b5563',
  },
  requestTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  detailsButtonText: {
    fontSize: 14,
    color: '#2563eb',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#dc2626',
  },
  requestDate: {
    fontSize: 14,
    color: '#6b7280',
  },
  expandedContent: {
    marginTop: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 24,
  },
  notesSection: {
    gap: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  addNoteContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-end',
  },
  noteInput: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
    minHeight: 60,
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  addNoteButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignSelf: 'flex-end',
  },
  addNoteButtonDisabled: {
    opacity: 0.5,
  },
  addNoteButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  notesList: {
    gap: 8,
  },
  noNotesText: {
    fontSize: 14,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  noteItem: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  noteContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  deleteNoteButton: {
    padding: 4,
    marginLeft: 8,
  },
  noteDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  attachmentsSection: {
    gap: 16,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 16,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 672,
    maxHeight: '90%',
    ...(Platform.OS === 'web' && {
      ...shadows.lg,
    }),
  },
  modalScrollContent: {
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseButton: {
    padding: 4,
  },
  form: {
    marginBottom: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 12,
  },
  priorityOptions: {
  },
  priorityOption: {
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#e5e7eb',
    borderRadius: 12,
    padding: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  priorityOptionSelected: {
    borderColor: '#000000',
    backgroundColor: '#f9fafb',
  },
  priorityOptionContent: {
  },
  priorityOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  priorityOptionLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
  },
  priorityOptionLabelSelected: {
    color: '#000000',
  },
  priorityOptionDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  priorityOptionDescriptionSelected: {
    color: '#4b5563',
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  pickerButtonText: {
    fontSize: 16,
    color: '#111827',
    flex: 1,
  },
  homeLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  homeLoadingText: {
    fontSize: 14,
    color: '#6b7280',
  },
  homeCardsContainer: {
    gap: 12,
    marginBottom: 24,
  },
  homeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 20,
    borderWidth: 2,
    borderColor: '#e5e7eb',
    borderRadius: 12,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeCardSelected: {
    borderColor: '#000000',
    backgroundColor: '#f9fafb',
  },
  homeCardContent: {
    flex: 1,
    gap: 4,
  },
  homeCardAddress: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  homeCardLocation: {
    fontSize: 14,
    color: '#6b7280',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  successContainer: {
    gap: 16,
  },
  successBox: {
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#bbf7d0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#166534',
    marginBottom: 4,
  },
  successText: {
    fontSize: 14,
    color: '#15803d',
    marginTop: 4,
  },
  doneButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  doneButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  doneButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '50%',
    ...(Platform.OS === 'web' && {
      ...shadows.bottom,
    }),
  },
  pickerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerModalList: {
    maxHeight: 300,
  },
  pickerModalItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  pickerModalItemText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerModalItemTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
  quotesSection: {
    gap: 16,
  },
  quotesList: {
    gap: 12,
  },
  quoteCard: {
    backgroundColor: '#f9fafb',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 8,
  },
  quoteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  quoteInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  quoteProviderName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
  },
  quoteAmount: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  quoteNotes: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  quoteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quoteMetaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  quoteCreated: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 4,
  },
  attachmentsContainer: {
    gap: 8,
    marginTop: 8,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    padding: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  attachmentContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  attachmentInfo: {
    flex: 1,
    gap: 2,
  },
  attachmentName: {
    fontSize: 13,
    color: '#111827',
    fontWeight: '500',
  },
  attachmentSize: {
    fontSize: 11,
    color: '#6b7280',
  },
  attachmentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  attachmentAction: {
    padding: 4,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  imagePreviewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePreviewClose: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePreviewCloseText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: '600',
  },
  imagePreview: {
    width: '90%',
    height: '90%',
  } as any,
  // Wizard styles
  stepIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumberActive: {
    backgroundColor: '#000000',
  },
  stepNumberText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  stepNumberTextActive: {
    color: '#ffffff',
  },
  stepLine: {
    flex: 1,
    height: 2,
    backgroundColor: '#e5e7eb',
    marginHorizontal: 8,
    maxWidth: 80,
  },
  stepLineActive: {
    backgroundColor: '#16a34a',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 24,
    lineHeight: 24,
  },
  introContainer: {
    gap: 24,
    paddingVertical: 16,
  },
  introTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 8,
  },
  introFeature: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  introFeatureText: {
    flex: 1,
    gap: 4,
  },
  introFeatureTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  introFeatureDescription: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
  },
  confirmationContainer: {
    alignItems: 'center',
    gap: 24,
    paddingVertical: 32,
  },
  confirmationIcon: {
    marginBottom: 8,
  },
  confirmationTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
  },
  confirmationText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 500,
  },
  attachmentSection: {
    width: '100%',
    gap: 12,
    marginTop: 16,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  attachmentSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  attachmentSectionSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  stepActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 'auto',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  primaryButtonDisabled: {
    opacity: 0.5,
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '500',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    marginTop: 16,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: '#DC2626',
    lineHeight: 20,
  },
  titleUpdateContainer: {
    padding: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    marginTop: 8,
  },
  titleUpdateText: {
    fontSize: 14,
    color: '#1E40AF',
    textAlign: 'center',
    fontWeight: '500',
  },
  nextStepsContainer: {
    width: '100%',
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 24,
    marginTop: 8,
    gap: 16,
  },
  nextStepsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  nextStepsList: {
    gap: 16,
  },
  nextStepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  nextStepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  nextStepNumberText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  nextStepText: {
    flex: 1,
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
    paddingTop: 3,
  },
  singleQuestionContainer: {
    gap: 20,
  },
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  questionHeaderText: {
    flex: 1,
    fontSize: 14,
    color: '#1E40AF',
    lineHeight: 20,
  },
  questionBlock: {
    gap: 12,
  },
  questionText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    lineHeight: 24,
  },
  answerInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    backgroundColor: '#FFFFFF',
    minHeight: 120,
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  answerInputFilled: {
    borderColor: '#2563EB',
    backgroundColor: '#F9FAFB',
  },
  questionActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 8,
  },
});

export default ServiceRequests;
